import { supabase } from '../lib/supabase';

export const pngNewsService = {
  // ============================================================================
  // NEWS RETRIEVAL
  // ============================================================================

  async getAllNews(filters = {}) {
    let query = supabase
      ?.from('png_government_news')
      ?.select('*')
      ?.eq('is_published', true)
      ?.order('publication_date', { ascending: false });

    // Apply category filter
    if (filters?.category && filters?.category !== 'all') {
      query = query?.eq('category', filters?.category);
    }

    // Apply priority filter
    if (filters?.priority) {
      query = query?.eq('priority', filters?.priority);
    }

    // Apply search filter
    if (filters?.search) {
      query = query?.or(`title.ilike.%${filters?.search}%,summary.ilike.%${filters?.search}%,content.ilike.%${filters?.search}%`);
    }

    // Apply date range filter
    if (filters?.dateFrom) {
      query = query?.gte('publication_date', filters?.dateFrom);
    }
    if (filters?.dateTo) {
      query = query?.lte('publication_date', filters?.dateTo);
    }

    // Apply featured filter
    if (filters?.featuredOnly) {
      query = query?.eq('is_featured', true);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      title: row?.title,
      summary: row?.summary,
      content: row?.content,
      category: row?.category,
      priority: row?.priority,
      source: row?.source,
      sourceUrl: row?.source_url,
      publicationDate: row?.publication_date,
      effectiveDate: row?.effective_date,
      gazetteReference: row?.gazette_reference,
      tags: row?.tags || [],
      affectedDepartments: row?.affected_departments || [],
      isFeatured: row?.is_featured,
      viewCount: row?.view_count,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getFeaturedNews(limit = 3) {
    const { data, error } = await supabase
      ?.from('png_government_news')
      ?.select('*')
      ?.eq('is_published', true)
      ?.eq('is_featured', true)
      ?.order('publication_date', { ascending: false })
      ?.limit(limit);

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      title: row?.title,
      summary: row?.summary,
      content: row?.content,
      category: row?.category,
      priority: row?.priority,
      source: row?.source,
      sourceUrl: row?.source_url,
      publicationDate: row?.publication_date,
      effectiveDate: row?.effective_date,
      gazetteReference: row?.gazette_reference,
      tags: row?.tags || [],
      affectedDepartments: row?.affected_departments || [],
      isFeatured: row?.is_featured,
      viewCount: row?.view_count,
      createdAt: row?.created_at
    })) || [];
  },

  async getNewsById(id) {
    const { data, error } = await supabase
      ?.from('png_government_news')
      ?.select('*')
      ?.eq('id', id)
      ?.single();

    if (error) throw error;

    // Increment view count
    await this.incrementViewCount(id);

    return {
      id: data?.id,
      title: data?.title,
      summary: data?.summary,
      content: data?.content,
      category: data?.category,
      priority: data?.priority,
      source: data?.source,
      sourceUrl: data?.source_url,
      publicationDate: data?.publication_date,
      effectiveDate: data?.effective_date,
      gazetteReference: data?.gazette_reference,
      tags: data?.tags || [],
      affectedDepartments: data?.affected_departments || [],
      isFeatured: data?.is_featured,
      viewCount: data?.view_count,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async getNewsByCategory(category) {
    const { data, error } = await supabase
      ?.from('png_government_news')
      ?.select('*')
      ?.eq('is_published', true)
      ?.eq('category', category)
      ?.order('publication_date', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      title: row?.title,
      summary: row?.summary,
      category: row?.category,
      priority: row?.priority,
      publicationDate: row?.publication_date,
      effectiveDate: row?.effective_date,
      gazetteReference: row?.gazette_reference,
      isFeatured: row?.is_featured
    })) || [];
  },

  async getRecentNews(limit = 5) {
    const { data, error } = await supabase
      ?.from('png_government_news')
      ?.select('*')
      ?.eq('is_published', true)
      ?.order('publication_date', { ascending: false })
      ?.limit(limit);

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      title: row?.title,
      summary: row?.summary,
      category: row?.category,
      priority: row?.priority,
      publicationDate: row?.publication_date,
      source: row?.source
    })) || [];
  },

  // ============================================================================
  // NEWS STATISTICS
  // ============================================================================

  async getNewsStats() {
    const { data, error } = await supabase
      ?.from('png_government_news')
      ?.select('category, priority, is_featured')
      ?.eq('is_published', true);

    if (error) throw error;

    const stats = {
      total: data?.length || 0,
      featured: data?.filter(n => n?.is_featured)?.length || 0,
      critical: data?.filter(n => n?.priority === 'critical')?.length || 0,
      byCategory: {
        hr: data?.filter(n => n?.category === 'hr')?.length || 0,
        payroll: data?.filter(n => n?.category === 'payroll')?.length || 0,
        tax: data?.filter(n => n?.category === 'tax')?.length || 0,
        minimumWage: data?.filter(n => n?.category === 'minimum_wage')?.length || 0,
        employmentAct: data?.filter(n => n?.category === 'employment_act')?.length || 0,
        gazette: data?.filter(n => n?.category === 'gazette')?.length || 0,
        general: data?.filter(n => n?.category === 'general')?.length || 0
      }
    };

    return stats;
  },

  // ============================================================================
  // VIEW TRACKING
  // ============================================================================

  async incrementViewCount(newsId) {
    const { error } = await supabase
      ?.rpc('increment_news_view_count', { news_id: newsId })
      ?.catch(() => {
        // Fallback if RPC doesn't exist
        return supabase
          ?.from('png_government_news')
          ?.update({ view_count: supabase?.raw('view_count + 1') })
          ?.eq('id', newsId);
      });

    if (error) console.error('Failed to increment view count:', error);
  },

  // ============================================================================
  // REALTIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToNews(callback) {
    const channel = supabase
      ?.channel('png_government_news_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'png_government_news',
          filter: 'is_published=eq.true'
        },
        (payload) => {
          const newNews = {
            id: payload?.new?.id,
            title: payload?.new?.title,
            summary: payload?.new?.summary,
            content: payload?.new?.content,
            category: payload?.new?.category,
            priority: payload?.new?.priority,
            source: payload?.new?.source,
            publicationDate: payload?.new?.publication_date,
            effectiveDate: payload?.new?.effective_date,
            gazetteReference: payload?.new?.gazette_reference,
            isFeatured: payload?.new?.is_featured
          };
          callback(newNews);
        }
      )
      ?.subscribe();

    return channel;
  },

  unsubscribeFromNews(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};

export default pngNewsService;